import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default settings
const DEFAULT_SETTINGS = {
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  timezone: 'America/New_York',
  primary_color: '#06b6d4',
  secondary_color: '#8b5cf6'
};

const SettingsContext = createContext();

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchSettings = useCallback(async () => {
    // Only fetch if we have an auth token
    const token = localStorage.getItem('token');
    if (!token) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings({
        ...DEFAULT_SETTINGS,
        ...response.data,
        date_format: response.data.date_format || DEFAULT_SETTINGS.date_format,
        time_format: response.data.time_format || DEFAULT_SETTINGS.time_format,
        timezone: response.data.timezone || DEFAULT_SETTINGS.timezone,
        primary_color: response.data.primary_color || DEFAULT_SETTINGS.primary_color,
        secondary_color: response.data.secondary_color || DEFAULT_SETTINGS.secondary_color
      });
    } catch (error) {
      // Only log error if it's not a 401/403 (which is expected when not authenticated)
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.error('Failed to fetch settings:', error);
      }
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check for authentication on mount and when storage changes
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const wasAuthenticated = isAuthenticated;
      const nowAuthenticated = !!token;
      
      setIsAuthenticated(nowAuthenticated);
      
      // Only fetch settings when we transition from not-authenticated to authenticated
      // or on initial load if already authenticated
      if (nowAuthenticated && !wasAuthenticated) {
        fetchSettings();
      } else if (!nowAuthenticated) {
        setSettings(DEFAULT_SETTINGS);
        setLoading(false);
      }
    };
    
    // Check auth on mount
    checkAuth();
    
    // Listen for storage changes (e.g., login/logout in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom auth events (for same-tab login/logout)
    const handleAuthChange = () => {
      checkAuth();
    };
    
    window.addEventListener('authChange', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, [fetchSettings, isAuthenticated]);

  // Format a date string according to user preferences
  const formatDate = useCallback((dateInput, options = {}) => {
    if (!dateInput) return '';
    
    try {
      let date;
      
      // Check if it's a date-only string (YYYY-MM-DD)
      // These should be treated as local dates without timezone conversion
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        // Parse as local date by adding T00:00:00 without timezone
        const [year, month, day] = dateInput.split('-');
        // Create date using local timezone (not UTC)
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      }
      
      if (isNaN(date.getTime())) return dateInput;

      const format = options.format || settings.date_format;
      
      // For date-only inputs, don't apply timezone conversion - use local date components
      const isDateOnly = typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput);
      
      // Get date components
      let year, month, monthShort, monthLong, day;
      
      if (isDateOnly) {
        // Use local date methods for date-only strings
        year = date.getFullYear().toString();
        month = String(date.getMonth() + 1).padStart(2, '0');
        day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthNamesLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        monthShort = monthNames[date.getMonth()];
        monthLong = monthNamesLong[date.getMonth()];
      } else {
        // Use timezone-aware formatting for datetime strings
        const tz = options.timezone || settings.timezone;
        const dateOptions = { timeZone: tz };
        year = date.toLocaleString('en-US', { ...dateOptions, year: 'numeric' });
        month = date.toLocaleString('en-US', { ...dateOptions, month: '2-digit' });
        monthShort = date.toLocaleString('en-US', { ...dateOptions, month: 'short' });
        monthLong = date.toLocaleString('en-US', { ...dateOptions, month: 'long' });
        day = date.toLocaleString('en-US', { ...dateOptions, day: '2-digit' });
      }

      switch (format) {
        case 'MM/DD/YYYY':
          return `${month}/${day}/${year}`;
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MMM DD, YYYY':
          return `${monthShort} ${day}, ${year}`;
        case 'DD MMM YYYY':
          return `${day} ${monthShort} ${year}`;
        case 'MMMM DD, YYYY':
          return `${monthLong} ${day}, ${year}`;
        default:
          return `${month}/${day}/${year}`;
      }
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateInput;
    }
  }, [settings.date_format, settings.timezone]);

  // Format a time string according to user preferences
  const formatTime = useCallback((timeInput, options = {}) => {
    if (!timeInput) return '';
    
    try {
      const format = options.format || settings.time_format;
      const tz = options.timezone || settings.timezone;
      
      // Handle time string like "14:30" or "2:30 PM"
      let hours, minutes;
      
      if (typeof timeInput === 'string') {
        // Check if it's a time string like "14:30" or "09:00"
        const timeMatch = timeInput.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (timeMatch) {
          hours = parseInt(timeMatch[1], 10);
          minutes = parseInt(timeMatch[2], 10);
        } else {
          // Try parsing as a full date/time
          const date = new Date(timeInput);
          if (!isNaN(date.getTime())) {
            const timeStr = date.toLocaleString('en-US', { 
              timeZone: tz, 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            });
            const parts = timeStr.split(':');
            hours = parseInt(parts[0], 10);
            minutes = parseInt(parts[1], 10);
          } else {
            return timeInput;
          }
        }
      } else if (timeInput instanceof Date) {
        const timeStr = timeInput.toLocaleString('en-US', { 
          timeZone: tz, 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        const parts = timeStr.split(':');
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      }

      if (hours === undefined || minutes === undefined) return timeInput;

      const paddedMinutes = minutes.toString().padStart(2, '0');

      if (format === '24h') {
        return `${hours.toString().padStart(2, '0')}:${paddedMinutes}`;
      } else {
        // 12-hour format
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${paddedMinutes} ${period}`;
      }
    } catch (error) {
      console.error('Time formatting error:', error);
      return timeInput;
    }
  }, [settings.time_format, settings.timezone]);

  // Format a full datetime
  const formatDateTime = useCallback((dateTimeInput, options = {}) => {
    if (!dateTimeInput) return '';
    
    try {
      const date = typeof dateTimeInput === 'string' ? new Date(dateTimeInput) : dateTimeInput;
      if (isNaN(date.getTime())) return dateTimeInput;

      const dateStr = formatDate(date, options);
      const timeStr = formatTime(date, options);
      
      return `${dateStr} ${timeStr}`;
    } catch (error) {
      console.error('DateTime formatting error:', error);
      return dateTimeInput;
    }
  }, [formatDate, formatTime]);

  // Refresh settings (call this after updating settings)
  const refreshSettings = useCallback(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      formatDate,
      formatTime,
      formatDateTime,
      refreshSettings
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
